import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { type Session, login } from '../api/auth.ts';
import { ApiError, errorMessage, isContractMismatch } from '../api/client.ts';
import { SESSION_QUERY_KEY } from '../auth/sessionContext.ts';
import { resolveRedirectTarget } from '../auth/redirectTarget.ts';
import { HelpLink } from '../components/HelpLink.tsx';
import { LogoLockup } from '../components/Logo.tsx';
import { Button, ErrorText, Field } from '../components/primitives.tsx';
import { ThemeMenu, ThemeRejectionNotice } from '../theme/ThemeMenu.tsx';

/**
 * Sign in.
 *
 * The endpoint it posts to is being built in parallel (`BACKLOG.md` O1–O3), and until it exists
 * every request lands on `server/src/app.ts`'s deny-by-default gate and comes back 401. That is
 * the correct behaviour for a system with no way to authenticate yet, and this page reports it as
 * what it is: an invalid-credentials message for a real 401, and a distinct "the API did not
 * answer as this app expects" for a 404 or a shape mismatch. Folding the second into the first
 * would make a missing endpoint look like a wrong password.
 *
 * **A 401 needs its own wording here, not `apiRequest`'s generic one.** Driven against the real
 * running server while `/api/auth/login` was still outside `PUBLIC_API_PATHS`, the client's
 * default copy for `unauthenticated` — "Your session has ended. Sign in again to continue." — is
 * correct for `RequireAuth`'s session check and wrong on this page: a first-time visitor cannot
 * have had a session end. `loginErrorMessage` below is what this page shows instead, for the one
 * status code a genuinely wrong password and a not-yet-open gate currently share.
 *
 * The theme control is on this page deliberately. A user who works in dark mode should not have to
 * sign in through a white screen first — which is why the preference lives in `localStorage`
 * rather than on the server profile (`theme/preference.ts`).
 *
 * **No `LanguageMenu` here, unlike `ThemeMenu`.** The UI language is a server-side account
 * preference (`i18n/useSyncAccountLocale.ts`) reachable only once a session exists, so there is
 * nothing for a picker to read or write pre-login — `i18n/index.ts`'s own module-load `init()`
 * already applies the browser-language half of the fallback chain here, and the picker itself lives
 * only in the authenticated account menu.
 */
/**
 * This page's own wording for a login failure, as distinct from `apiRequest`'s session-expiry
 * copy. A plain 401 — whichever of "wrong password" or "the gate is not open yet" it turns out to
 * be — reads here as a credentials problem, since that is what a finished endpoint's 401 means.
 *
 * Takes `t` as a parameter rather than calling `useTranslation` itself — this is a plain function,
 * not a component or hook, so it cannot call one.
 */
function loginErrorMessage(error: unknown, t: TFunction<'login'>): string {
  if (error instanceof ApiError && error.status === 401) {
    return t('invalidCredentials');
  }
  return errorMessage(error);
}

export function LoginPage() {
  const { t } = useTranslation('login');
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // `location.state` is `any` as far as the router's types are concerned, so it is pinned to
  // `unknown` before anything reads it. Narrowing by `in` rather than casting keeps the
  // no-type-escape-hatch invariant intact, and `resolveRedirectTarget` takes `unknown` precisely
  // because this value can be anything a previous navigation put there.
  const routerState: unknown = location.state;
  const fromState =
    typeof routerState === 'object' && routerState !== null && 'from' in routerState
      ? routerState.from
      : undefined;

  const target = resolveRedirectTarget({
    fromState,
    fromQuery: searchParams.get('next'),
  });

  const signIn = useMutation({
    mutationFn: login,
    onSuccess: (session: Session) => {
      // Seed the cache so the guard on the next route does not have to round-trip to discover
      // something this response already told us.
      queryClient.setQueryData(SESSION_QUERY_KEY, session);
      void navigate(target, { replace: true });
    },
  });

  const existing = queryClient.getQueryData<Session>(SESSION_QUERY_KEY);
  if (existing !== undefined && !signIn.isPending) {
    return <Navigate to={target} replace />;
  }

  return (
    <main id="main" className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center p-4">
      <div className="mb-6 flex items-center justify-between">
        <LogoLockup size={40} />
        <ThemeMenu />
      </div>
      <ThemeRejectionNotice />

      <div className="rounded-panel border border-line bg-surface p-4 shadow-panel sm:p-6">
        <div className="mb-1 flex items-center gap-2">
          <h1 className="m-0 text-xl font-semibold text-ink">{t('heading')}</h1>
          <HelpLink topicId="signing-in" />
        </div>
        <p className="mt-0 mb-5 text-sm text-ink-muted">{t('subtitle')}</p>

        <form
          // Inline so the event type is inferred. React 19's own types deprecate the named
          // `FormEvent` alias, and importing a deprecated type to annotate a handler is a lint
          // error here rather than a warning.
          onSubmit={(event) => {
            event.preventDefault();
            signIn.mutate({ email, password });
          }}
          className="flex flex-col gap-4"
        >
          <Field
            label={t('emailLabel')}
            type="email"
            name="email"
            required
            autoComplete="username"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
          <Field
            label={t('passwordLabel')}
            type="password"
            name="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />

          {signIn.isError ? (
            isContractMismatch(signIn.error) ? (
              <div role="alert" className="rounded-control border border-danger p-3 text-sm">
                <p className="m-0 font-semibold text-ink">{t('contractMismatch.title')}</p>
                <p className="m-0 mt-1 text-ink">{errorMessage(signIn.error)}</p>
                <p className="m-0 mt-1 text-ink-muted">{t('contractMismatch.detail')}</p>
              </div>
            ) : (
              <ErrorText message={loginErrorMessage(signIn.error, t)} />
            )
          ) : null}

          <Button type="submit" pending={signIn.isPending} className="w-full">
            {signIn.isPending ? t('submitting') : t('submit')}
          </Button>
        </form>
      </div>
    </main>
  );
}
